'use client'

import { useExtracted, useLocale } from 'next-intl'
import Link from 'next/link'
import { ExternalLink } from '@/components/ui/icons'
import { DocsShell } from '@/components/docs/DocsShell'
import { LearnVideoPlayer } from '@/components/learn/LearnVideoPlayer'
import { toDocsLocale } from '@/lib/docs'
import { getCommunityLinks, getLessons, type CommunityLink, type Lesson } from '../learn-data'
import { LEARN_INDEX_COPY, LESSON_PAGE_COPY } from '../learn-copy'
import { usePublishedOverrides } from '@/i18n/useContentOverrides'
import {
  applyCommunityLinkOverrides,
  applyLearnIndexCopyOverrides,
  applyLessonOverrides,
  applyLessonPageCopyOverrides,
} from '@/lib/i18n/learnRouteMessages'

/**
 * The Tutorials landing: a gallery of every video lesson, in the same coordinated
 * grid the community page uses. Each card links to its lesson page (where the
 * video plays full-width). Replaces the old redirect-to-first-lesson behavior so
 * the Tutorials tab has a real overview.
 */
export default function TutorialsPage() {
  const locale = toDocsLocale(useLocale())
  const overrides = usePublishedOverrides(locale)
  const index = applyLearnIndexCopyOverrides(LEARN_INDEX_COPY[locale], overrides)
  const lessonCopy = applyLessonPageCopyOverrides(LESSON_PAGE_COPY[locale], overrides)
  const lessons = getLessons(locale).map(lesson => applyLessonOverrides(lesson, overrides))
  // The intro (number 0) is featured above the grid; the numbered
  // walkthrough lessons fill the grid itself.
  const intro = lessons.find(lesson => lesson.slug === 'start-here')
  const gridLessons = lessons.filter(lesson => lesson.slug !== 'start-here')
  const communityLinks = applyCommunityLinkOverrides(getCommunityLinks(locale), overrides)

  return (
    <DocsShell>
      <header className="mb-10">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">{index.tutorials}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{index.tutorialsDescription}</p>
      </header>

      {intro && <FeaturedIntro lesson={intro} />}

      <div className="grid grid-cols-1 gap-x-4 gap-y-10 sm:grid-cols-2">
        {gridLessons.map(lesson => (
          <LessonCard key={lesson.slug} lesson={lesson} lessonLabel={lessonCopy.lesson} />
        ))}
      </div>

      {communityLinks.length > 0 && (
        <section className="mt-14">
          <h2 className="mb-5 text-xs font-medium uppercase tracking-wider text-muted-foreground/50">
            {index.community}
          </h2>
          <div className="grid grid-cols-1 gap-x-4 gap-y-10 sm:grid-cols-2">
            {communityLinks.map(link => (
              <CommunityCard key={link.href} link={link} />
            ))}
          </div>
        </section>
      )}
    </DocsShell>
  )
}

/**
 * The intro lesson (number 0), featured above the lesson grid at full column
 * width — twice the size of a lesson card — so newcomers start with the
 * introduction before picking a lesson. Plays inline; the title links to its
 * lesson page at /learn/tutorials/start-here.
 */
function FeaturedIntro({ lesson }: { lesson: Lesson }) {
  const t = useExtracted()
  return (
    <section className="mb-12">
      <LearnVideoPlayer src={lesson.videoUrl} title={lesson.title} />
      <div className="mt-2.5 px-0.5">
        <p className="text-xs text-muted-foreground/60">
          {t('Start here')}
          {lesson.duration && (
            /* eslint-disable-next-line i18next/no-literal-string -- decorative separator */
            <span className="text-muted-foreground/40"> · {lesson.duration}</span>
          )}
        </p>
        <h2 className="mt-0.5 text-lg font-semibold text-foreground">
          <Link href={`/learn/tutorials/${lesson.slug}`} className="transition-colors hover:text-muted-foreground">
            {lesson.title}
          </Link>
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{lesson.description}</p>
      </div>
    </section>
  )
}

function LessonCard({ lesson, lessonLabel }: { lesson: Lesson; lessonLabel: string }) {
  return (
    <Link href={`/learn/tutorials/${lesson.slug}`} className="group block">
      <div className="relative aspect-video overflow-hidden rounded-xl border border-white/10 bg-card">
        {/* eslint-disable-next-line @next/next/no-img-element -- external thumbnail, parity with community gallery */}
        <img
          src={lesson.thumbnailUrl}
          alt={lesson.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        {lesson.duration && (
          <span className="absolute bottom-2 end-2 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
            {lesson.duration}
          </span>
        )}
      </div>

      <div className="mt-2.5 px-0.5">
        <p className="text-xs text-muted-foreground/60">
          {lessonLabel} {lesson.number}
        </p>
        <h3 className="mt-0.5 text-base font-medium text-foreground transition-colors group-hover:text-foreground">
          {lesson.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{lesson.description}</p>
      </div>
    </Link>
  )
}

function CommunityCard({ link }: { link: CommunityLink }) {
  return (
    <a href={link.href} target="_blank" rel="noopener noreferrer" className="group block">
      <div className="relative aspect-video overflow-hidden rounded-xl border border-white/10 bg-card">
        {/* eslint-disable-next-line @next/next/no-img-element -- external thumbnail, parity with community gallery */}
        <img
          src={link.thumbnailUrl}
          alt={link.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>

      <div className="mt-2.5 px-0.5">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium text-foreground">{link.title}</h3>
          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/40" />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground/60">
          {link.author}
          {/* eslint-disable-next-line i18next/no-literal-string -- decorative separator */}
          {link.meta && <span className="text-muted-foreground/40"> · {link.meta}</span>}
        </p>
        <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{link.description}</p>
      </div>
    </a>
  )
}
